import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import CustomSelect from 'components/custom-select';
import { useModal } from 'context/ModalContext';
import { requestTaskDeletion } from 'services/dashboard.service';

import {
  DELETE_REASON_OPTIONS,
  DELETE_SUCCESS_MESSAGE,
  DeleteReasonOption,
  GENERIC_DELETE_ERROR_MESSAGE,
  MAX_OTHER_REASON_LENGTH,
  formatReasonCounter,
  validateDeleteReason,
} from '../delete-rules';
import { CAMPO_DA_ACAO, ConfirmacaoDaAcao } from './moldura-da-acao';

/** Textos homologados com o cliente em 2026-09-25. */
const MODAL_TITLE = 'Deseja excluir a Realização Destaque?';
const WITH_REASON_TEXT =
  'Você pode excluir a Realização Destaque em qualquer etapa de validação. Todas as informações e participantes serão excluídos, além de todos os pontos até aqui recebidos.';
const WITHOUT_REASON_TEXT = 'Todas as informações e participantes serão excluídos.';

const TOAST_OPTIONS = {
  position: 'top-right' as const,
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

const REASON_OPTIONS = [...DELETE_REASON_OPTIONS];

const ROTULO = 'mb-1.5 block text-jj-label font-semibold text-content-soft';

export function deleteTaskModalId(taskId: string) {
  return `delete-task-${taskId}`;
}

interface Props {
  taskId: string;
  /** Regra do front (`deleteRequiresReason`). O servidor pode exigir o motivo mesmo quando ela diz que não. */
  requiresReason: boolean;
  onDeleted: () => void;
  onRefused: () => void;
}

/**
 * Confirmação da exclusão da Realização Destaque pelo cadastrante, na moldura dos modais de ação da nova
 * identidade. A exclusão é o action_log `task_delete`; quem decide o desfecho é a trigger
 * (spec `exclusao-realizacao-action-log`).
 */
export default function ModalDeleteTask({ taskId, requiresReason, onDeleted, onRefused }: Props) {
  const { openModal, closeModal } = useModal();
  const isOpen = openModal === deleteTaskModalId(taskId);

  const [askReason, setAskReason] = useState(requiresReason);
  const [option, setOption] = useState<DeleteReasonOption | undefined>();
  const [otherText, setOtherText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setAskReason(requiresReason);
    setOption(undefined);
    setOtherText('');
  }, [isOpen, requiresReason]);

  const reason = useMemo(() => validateDeleteReason({ option, text: otherText }), [option, otherText]);
  const canConfirm = !sending && (!askReason || reason.valid);
  const reasonInputId = `delete-task-reason-${taskId}`;
  const otherInputId = `delete-task-other-${taskId}`;

  function close() {
    if (!sending) closeModal();
  }

  async function handleConfirm() {
    if (!canConfirm) return;

    setSending(true);

    try {
      const result = await requestTaskDeletion(
        askReason ? { taskId, reason: reason.reason, reasonOption: reason.reasonOption } : { taskId },
      );
      setSending(false);

      if (result.ok) {
        toast.success(DELETE_SUCCESS_MESSAGE, TOAST_OPTIONS);
        closeModal();
        onDeleted();
        return;
      }

      toast.error(result.message, TOAST_OPTIONS);

      if (result.status === 'REASON_REQUIRED') {
        setAskReason(true);
        return;
      }

      closeModal();
      onRefused();
    } catch (error) {
      setSending(false);
      toast.error(GENERIC_DELETE_ERROR_MESSAGE, TOAST_OPTIONS);
    }
  }

  if (!isOpen) return null;

  return (
    <ConfirmacaoDaAcao
      title={MODAL_TITLE}
      onClose={close}
      closeDisabled={sending}
      backLabel="Cancelar"
      backDisabled={sending}
      onConfirm={handleConfirm}
      confirmDisabled={!canConfirm}
    >
      <p className="m-0">{askReason ? WITH_REASON_TEXT : WITHOUT_REASON_TEXT}</p>

      {askReason && (
        <div>
          <label htmlFor={reasonInputId} className={ROTULO}>
            Motivo
          </label>
          <CustomSelect
            inputId={reasonInputId}
            ariaLabel="Motivo da exclusão"
            variant="light"
            placeholder="Selecione o motivo da exclusão"
            options={REASON_OPTIONS}
            value={REASON_OPTIONS.find(item => item.value === option) ?? null}
            onChange={(selected: any) => setOption(selected?.value)}
            disabled={sending}
          />
        </div>
      )}

      {askReason && option === 'outros' && (
        <div>
          <label htmlFor={otherInputId} className={ROTULO}>
            Descreva o motivo
          </label>
          <input
            id={otherInputId}
            value={otherText}
            maxLength={MAX_OTHER_REASON_LENGTH}
            placeholder="Escreva o motivo"
            onChange={event => setOtherText(event.target.value)}
            disabled={sending}
            aria-describedby={`${otherInputId}-counter`}
            className={CAMPO_DA_ACAO}
          />
          {/* D3: 10px não tem degrau no Tailwind. O mínimo explica por que "Confirmar" segue desabilitado (WCAG 3.3.2). */}
          <p id={`${otherInputId}-counter`} aria-live="polite" className="m-0 mt-1.5 text-[10px] text-content-soft">
            {formatReasonCounter(reason.length)}
          </p>
        </div>
      )}
    </ConfirmacaoDaAcao>
  );
}
